import React, { useState, useEffect, useMemo } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import IntlMessages from "@iso/components/utility/intlMessages";
import VisitorFormWrapper from "./VisitorForm.styles";
import { Button, message, Spin } from "antd";
import logo from "@iso/assets/images/logo.png";
import queryString from "query-string";
import { visitorFormService } from "@iso/services";
import VisitorFormModal from "./VisitorFormModal";

// Build the leading "Check-in Info" step that is prepended to a check-in form's
// schema so it renders as the FIRST step of the same stepper (before the form's
// own steps). Its fields are keyed under `_checkin.*` so VisitorFormModal can lift
// them out into the submission's `checkin_info` block and drop them from the schema
// answers. Business Name is required only for a contractor sign-in form.
const buildCheckinInfoStep = (isContractor) => ({
  id: "_checkin_info",
  title: "Check-in Info",
  formMeta: {
    columns: 1,
    fields: [
      { key: "_checkin.name", label: "Name", required: true, rules: [{ whitespace: true, message: "Please enter your name" }] },
      { key: "_checkin.phone", label: "Phone Number", required: true, rules: [{ whitespace: true, message: "Please enter your phone number" }] },
      { key: "_checkin.email", label: "Email", required: true, rules: [{ type: "email", message: "Please enter a valid email" }] },
      ...(isContractor
        ? [{ key: "_checkin.business_name", label: "Business / Company Name", required: true, rules: [{ whitespace: true, message: "Please enter your business name" }] }]
        : []),
      { key: "_checkin.purpose", label: "Purpose of Visit" },
    ],
  },
});

export default function VisitorForm() {
  const location = useLocation();
  const isLoggedIn = useSelector((state) => state.Auth.idToken);
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);
  const formToken = queryString.parse(window.location.search).token || "";
  const [formData, setFormData] = useState({});
  const [visibleVisitorFormModal, setVisibleVisitorFormModal] = useState(false);
  const [property, setProperty] = useState({});
  // The property's Areas, returned beside the property (not inside it — the
  // Property model is shared by every other caller). Suggestions for any
  // "Property Area / Location" field on this form or the induction after it.
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(!!formToken);

  useEffect(() => {
    if (isLoggedIn) {
      setRedirectToReferrer(true);
    }
  }, [isLoggedIn]);

  const { from } = location.state || { from: { pathname: "/dashboard" } };

  useEffect(() => {
    if (!formToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    visitorFormService
      .getVisitorForm(formToken)
      .then((res) => {
        if (res && res.code === "0000") {
          setFormData(res.form);
          setProperty(res.property);
          setAreas(res.areas || []);
          setVisibleVisitorFormModal(true);
        } else {
          message.error((res && res.message) || "This form is not available.");
        }
      })
      .catch(() => message.error("Failed to load the form."))
      .finally(() => setLoading(false));
  }, [formToken]);

  const isCheckinForm = !!(formData && formData.is_checkin_form);
  // Contractor check-in is decided by the form's PURPOSE (Contractor Check-in /
  // Check-out), not scope — that's what requires Business Name and drives the
  // contractor bridge.
  const isContractor = !!(formData && formData.purpose === "contractor_check_in");

  // For a check-in SCHEMA form, prepend the "Check-in Info" step so the guest fills
  // Name/Phone/Email/Purpose (+ Business for contractors) as step 1 of the stepper.
  const renderedForm = useMemo(() => {
    if (
      isCheckinForm &&
      formData &&
      formData.schema_json &&
      Array.isArray(formData.schema_json.steps)
    ) {
      return {
        ...formData,
        schema_json: {
          ...formData.schema_json,
          steps: [buildCheckinInfoStep(isContractor), ...formData.schema_json.steps],
        },
      };
    }
    return formData;
  }, [formData, isCheckinForm, isContractor]);

  if (redirectToReferrer) {
    return <Redirect to={from} />;
  }

  const cancelModal = () => {
    setVisibleVisitorFormModal(false);
    setFormData({});
  };

  return (
    <VisitorFormWrapper className="isoSignInPage">
      <div className="isoLoginContentWrapper">
        <div className="isoLoginContent">
          <div className="isoLogoWrapper">
            <Link to="/dashboard">
              <img src={logo} height="40" alt="AirAgri" />
            </Link>
          </div>

          {loading && (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin size="large" />
            </div>
          )}

          {/* Non-check-in forms keep the original sign-in / create-account links. */}
          {!loading && !isCheckinForm && (
            <div className="isoSignInForm">
              <Link to="/signin" className="isoForgotPass">
                <Button type="primary" className="btn-success">
                  <IntlMessages id="page.signInButton" />
                </Button>
              </Link>

              <div className="isoCenterComponent isoHelperWrapper">
                <Link to={`/signup`}>
                  <IntlMessages id="page.signInCreateAccount" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <VisitorFormModal
        visibleFormModal={visibleVisitorFormModal}
        cancelModal={cancelModal}
        propertyId={formData?.property_id}
        formData={renderedForm}
        property={property}
        areas={areas}
        formToken={formToken}
      />
    </VisitorFormWrapper>
  );
}
